/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { pause } from '@squiz/dxp-console-support';
import { TOAST_TYPE_DANGER, TOAST_TYPE_LOADING, TOAST_TYPE_SUCCESS } from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { create } from 'zustand';
import {
  ChangePermissionsResponse,
  GetStatusResponse,
  ListObjectsResponse,
  MakeDirectoryResponse,
  RemoveItemsResponse,
} from '../../FileStore.service.defs';
import {
  ChangePermissionsDto,
  DownloadItemDto,
  ExtractArchiveDto,
  ListDirectoriesDto,
  ListObjectsDto,
  MakeDirectoryDto,
  RemoveItemsDto,
  UploadFileDto,
  UploadFilesDto,
} from '../../FileStore.service.dto';
import { fileToBase64, getFileStoreService } from '../../Utilities';
import { toastFactory } from '../../Utilities/toastFactory/toastFactory';
import { USE_FILE_STORAGE_STRINGS } from './useFileStore.constants';
import { useFileStoreLoadingStates, useFileStoreReturnTypes } from './useFileStore.defs';

const useLoadingStates = create<useFileStoreLoadingStates>((set) => ({
  changePermissionsLoading: false,
  directoryContentsLoading: false,
  extractArchiveLoading: false,
  fileUploadLoading: false,
  listDirectoriesLoading: false,
  makeDirectoryLoading: false,
  removeItemsLoading: false,
  setChangePermissionsLoading: (loading: boolean): void => set({ changePermissionsLoading: loading }),
  setDirectoryContentsLoading: (loading: boolean): void => set({ directoryContentsLoading: loading }),
  setExtractArchiveLoading: (loading: boolean): void => set({ extractArchiveLoading: loading }),
  setFileUploadLoading: (loading: boolean): void => set({ fileUploadLoading: loading }),
  setListDirectoriesLoading: (loading: boolean): void => set({ listDirectoriesLoading: loading }),
  setMakeDirectoryLoading: (loading: boolean): void => set({ makeDirectoryLoading: loading }),
  setRemoveItemsLoading: (loading: boolean): void => set({ removeItemsLoading: loading }),
}));

export const useFileStore = (): useFileStoreReturnTypes => {
  const { t } = useTranslation();
  const fileStoreService = getFileStoreService();
  const labels = useMemo(() => {
    return {
      changedPermissionsToXFilesToastMsg: t(USE_FILE_STORAGE_STRINGS.changedPermissionsToXFilesToastMsg),
      deletedXFilesToastMsg: t(USE_FILE_STORAGE_STRINGS.deletedXElementsToastMsg),
      deletingFilesInProgress: t(USE_FILE_STORAGE_STRINGS.deletingFilesInProgress),
      downloadedElementToastMsg: t(USE_FILE_STORAGE_STRINGS.downloadedElementToastMsg),
      errorDeletingXElementsToastMsg: t(USE_FILE_STORAGE_STRINGS.errorDeletingXElementsToastMsg),
      errorDownloadingElementToastMsg: t(USE_FILE_STORAGE_STRINGS.errorDownloadingElementToastMsg),
      errorOccurred: t(USE_FILE_STORAGE_STRINGS.errorOccurred),
      errorUploadingXFilesToastMsg: t(USE_FILE_STORAGE_STRINGS.errorUploadingXFilesToastMsg),
      extractingArchiveInProgress: t(USE_FILE_STORAGE_STRINGS.extractingArchiveInProgress),
      extractingArchiveSuccess: t(USE_FILE_STORAGE_STRINGS.extractingArchiveSuccess),
      fileUploadCompleteToastMsg: t(USE_FILE_STORAGE_STRINGS.fileUploadCompleteToastMsg),
      fileUploadErrorToastMsg: t(USE_FILE_STORAGE_STRINGS.fileUploadErrorToastMsg),
      folderCreateSuccessToastMsg: t(USE_FILE_STORAGE_STRINGS.folderCreateSuccessToastMsg),
      uploadedXFilesToastMsg: t(USE_FILE_STORAGE_STRINGS.uploadedXFilesToastMsg),
    };
  }, [t]);

  const { updateActiveToast } = useToastStore();
  const {
    fileUploadLoading,
    setFileUploadLoading,
    setDirectoryContentsLoading,
    directoryContentsLoading,
    makeDirectoryLoading,
    removeItemsLoading,
    setRemoveItemsLoading,
    setMakeDirectoryLoading,
    changePermissionsLoading,
    setChangePermissionsLoading,
    extractArchiveLoading,
    setExtractArchiveLoading,
    listDirectoriesLoading,
    setListDirectoriesLoading,
  } = useLoadingStates();

  const loadingState = useMemo(() => {
    return {
      changePermissionsLoading,
      directoryContentsLoading,
      extractArchiveLoading,
      fileUploadLoading,
      listDirectoriesLoading,
      makeDirectoryLoading,
      removeItemsLoading,
    };
  }, [
    changePermissionsLoading,
    directoryContentsLoading,
    extractArchiveLoading,
    fileUploadLoading,
    listDirectoriesLoading,
    makeDirectoryLoading,
    removeItemsLoading,
  ]);

  // TODO: Handle case where some files succeed to upload, some don't
  const fileUpload = useCallback(
    async (dto: UploadFilesDto): Promise<void> => {
      const errorToast = toastFactory({ toastContent: <>{labels.fileUploadErrorToastMsg}</>, type: TOAST_TYPE_DANGER });
      const successToast = toastFactory({
        toastContent: <>{labels.fileUploadCompleteToastMsg}</>,
        type: TOAST_TYPE_SUCCESS,
      });
      setFileUploadLoading(true);
      updateActiveToast(null);

      const filesToUpload: Array<UploadFileDto> = [];

      for (const file of dto.files) {
        const data = await fileToBase64(file.file);

        filesToUpload.push({
          access: file.access,
          data,
          source: file.source,
        });
      }

      await lastValueFrom(fileStoreService.uploadFiles(filesToUpload))
        .then(async () => {
          // Wait for Dynamo records to sync up so we get correct icon/size
          await pause(3000);
          setFileUploadLoading(false);
          return updateActiveToast(successToast);
        })
        .catch((err) => {
          setFileUploadLoading(false);
          updateActiveToast(errorToast);
          return err;
        });
    },
    [
      fileStoreService,
      labels.fileUploadCompleteToastMsg,
      labels.fileUploadErrorToastMsg,
      setFileUploadLoading,
      updateActiveToast,
    ],
  );

  const getDirectoryContents = useCallback(
    async (dto: ListObjectsDto): Promise<ListObjectsResponse> => {
      setDirectoryContentsLoading(true);
      return await lastValueFrom(fileStoreService.listObjects(dto))
        .then((res) => {
          setDirectoryContentsLoading(false);
          return res;
        })
        .catch((err) => {
          setDirectoryContentsLoading(false);
          return err;
        });
    },
    [fileStoreService, setDirectoryContentsLoading],
  );

  const listDirectories = useCallback(
    async (dto: ListDirectoriesDto): Promise<ListObjectsResponse> => {
      setListDirectoriesLoading(true);
      return await lastValueFrom(fileStoreService.listDirectories(dto))
        .then((res) => {
          setListDirectoriesLoading(false);
          return res;
        })
        .catch((err) => {
          setListDirectoriesLoading(false);
          return err;
        });
    },
    [fileStoreService, setListDirectoriesLoading],
  );

  const makeDirectory = useCallback(
    async (dto: MakeDirectoryDto): Promise<MakeDirectoryResponse> => {
      const errorToast = toastFactory({ toastContent: <>{labels.errorOccurred}</>, type: TOAST_TYPE_DANGER });

      const successToast = toastFactory({
        toastContent: <>{labels.folderCreateSuccessToastMsg}</>,
        type: TOAST_TYPE_SUCCESS,
      });

      updateActiveToast(null);
      setMakeDirectoryLoading(true);
      return await lastValueFrom(fileStoreService.makeDirectory(dto))
        .then(() => {
          setMakeDirectoryLoading(false);
          return updateActiveToast(successToast);
        })
        .catch((err) => {
          setMakeDirectoryLoading(false);
          updateActiveToast(errorToast);
          return err;
        });
    },
    [
      fileStoreService,
      labels.errorOccurred,
      labels.folderCreateSuccessToastMsg,
      setMakeDirectoryLoading,
      updateActiveToast,
    ],
  );

  const removeItems = useCallback(
    async (dto: RemoveItemsDto, showLoadingToast?: boolean): Promise<RemoveItemsResponse> => {
      const errorToast = toastFactory({ toastContent: <>{labels.errorOccurred}</>, type: TOAST_TYPE_DANGER });
      const loadingToast = toastFactory({
        toastContent: <>{labels.deletingFilesInProgress}</>,
        type: TOAST_TYPE_LOADING,
      });
      if (showLoadingToast) {
        updateActiveToast(loadingToast);
      }
      setRemoveItemsLoading(true);
      return await lastValueFrom(fileStoreService.removeItems(dto))
        .then((res) => {
          if (!res) {
            throw new Error('No response from removeItems');
          }

          const successToast = toastFactory({
            toastContent: <>{labels.deletedXFilesToastMsg.replace('{{count}}', `${res.successes}`)}</>,
            type: TOAST_TYPE_SUCCESS,
          });

          if (res.successes === 0 && res.errors === 0) {
            updateActiveToast(errorToast);
            throw new Error('Unknown error occurred - no files deleted, no files errored');
          } else if (res.successes === 0 && res.errors > 0) {
            updateActiveToast(errorToast);
            throw new Error('Unknown error occurred - no files deleted, all files errored');
          }

          setRemoveItemsLoading(false);
          updateActiveToast(successToast);
          return res;
        })
        .catch((err) => {
          setRemoveItemsLoading(false);
          updateActiveToast(errorToast);
          return err;
        });
    },
    [fileStoreService, labels, setRemoveItemsLoading, updateActiveToast],
  );

  const downloadItem = useCallback(
    async (dto: DownloadItemDto): Promise<void> => {
      const { fileId, source } = dto;

      const downloadStartedToast = toastFactory({
        toastContent: <>{labels.downloadedElementToastMsg}</>,
        type: TOAST_TYPE_SUCCESS,
      });
      const errorToast = toastFactory({
        toastContent: <>{labels.errorDownloadingElementToastMsg}</>,
        type: TOAST_TYPE_DANGER,
      });

      const downloadURL = fileStoreService.createDownloadURL(fileId);

      if (!downloadURL) {
        updateActiveToast(errorToast);
        return;
      }

      const temporaryLink = document.createElement('a');
      temporaryLink.href = downloadURL;
      temporaryLink.download = source.split('/').pop() as string;
      document.body.appendChild(temporaryLink);
      temporaryLink.click();
      temporaryLink.remove();

      updateActiveToast(downloadStartedToast);
    },
    [fileStoreService, labels, updateActiveToast],
  );

  const changePermissions = useCallback(
    async (dto: ChangePermissionsDto): Promise<ChangePermissionsResponse> => {
      const errorToast = toastFactory({ toastContent: <>{labels.errorOccurred}</>, type: TOAST_TYPE_DANGER });
      updateActiveToast(null);
      setChangePermissionsLoading(true);

      return await lastValueFrom(fileStoreService.changePermissions(dto))
        .then((res) => {
          if (!res) {
            throw new Error('No response from changePermissions');
          }

          const successToast = toastFactory({
            toastContent: <>{labels.changedPermissionsToXFilesToastMsg.replace('{{count}}', `${res.successes}`)}</>,
            type: TOAST_TYPE_SUCCESS,
          });

          if (res.successes === 0 && res.errors === 0) {
            updateActiveToast(errorToast);
            throw new Error('Unknown error occurred - no files changed, no files errored');
          } else if (res.successes === 0 && res.errors > 0) {
            updateActiveToast(errorToast);
            throw new Error('Unknown error occurred - no files changed, all files errored');
          }

          setChangePermissionsLoading(false);
          updateActiveToast(successToast);
          return res;
        })
        .catch((err) => {
          setChangePermissionsLoading(false);
          updateActiveToast(errorToast);
          return err;
        });
    },
    [
      fileStoreService,
      labels.changedPermissionsToXFilesToastMsg,
      labels.errorOccurred,
      setChangePermissionsLoading,
      updateActiveToast,
    ],
  );

  const extractArchive = useCallback(
    async (dto: ExtractArchiveDto): Promise<GetStatusResponse> => {
      const errorToast = toastFactory({ toastContent: <>{labels.errorOccurred}</>, type: TOAST_TYPE_DANGER });
      const successToast = toastFactory({
        toastContent: <>{labels.extractingArchiveSuccess}</>,
        type: TOAST_TYPE_SUCCESS,
      });
      const loadingToast = toastFactory({
        toastContent: <>{labels.extractingArchiveInProgress}</>,
        type: TOAST_TYPE_LOADING,
      });

      updateActiveToast(null);
      updateActiveToast(loadingToast);
      setExtractArchiveLoading(true);

      return await lastValueFrom(fileStoreService.extractArchive(dto))
        .then((res) => {
          if (!res) {
            throw new Error('No response from extractArchive');
          }

          switch (res.status) {
            case 'SUCCEED':
              updateActiveToast(successToast);
              setExtractArchiveLoading(false);
              return res.data;
            case 'FAILED':
              updateActiveToast(errorToast);
              setExtractArchiveLoading(false);
              throw new Error('Extract operation failed');
            default:
              return res;
          }
        })
        .catch((err) => {
          setExtractArchiveLoading(false);
          updateActiveToast(errorToast);
          return err;
        });
    },
    [
      fileStoreService,
      labels.errorOccurred,
      labels.extractingArchiveInProgress,
      labels.extractingArchiveSuccess,
      setExtractArchiveLoading,
      updateActiveToast,
    ],
  );
  return {
    changePermissions,
    downloadItem,
    extractArchive,
    fileUpload,
    getDirectoryContents,
    listDirectories,
    loadingState,
    makeDirectory,
    removeItems,
  };
};
